/* eslint-disable no-case-declarations */
import React, {
    createContext,
    ReactNode,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useState,
} from 'react';
import moment from 'moment/moment';

import { useSelector as useReduxSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { notification } from 'ui-library';
import { AdvisoryOptionTypesEnum, useGetListItemsByKey } from 'hooks/rest/useGetListItemsByKey';
import { useGetStrategyAllocations } from 'hooks/rest/useGetStrategyAllocations';
import { useProductsList } from 'hooks/useProducts';
import { usePostAdvisoryProcessData } from 'hooks/rest/usePostAdvisoryProcessData';
import { AdvisoryMetadataResponse, AdvisoryResponse, InvestmentDescription } from 'core/types/api/Api';
import { ContactGroupEnum } from 'components/Connections/types';
import { TPageError } from 'datasource/PortfolioProcessLayout/PortfolioFlowLayoutManager.types';

import {
    APPLICATION_CONTAINER_ID,
    PRODUCT_VALUES_FOR_3A_VB,
    PRODUCT_VALUES_FOR_3A_VB_ENUM,
    PRODUCT_VALUES_FOR_3B,
    PRODUCTS,
    STATUS_OK,
    THREEBTYPES,
} from 'constants/constants';

import arrayContains from 'utils/arrayUtils';
import { useContactGroupOwners } from 'hooks/helpers/useContactGroupOwners';
import { useGetJointPortfolios } from 'hooks/rest/useGetJointPortfolios';
import { getPortfolioStatus } from 'constants/portfolioStatuses';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import { useCachedAPI } from 'hooks/useCachedAPI';
import {
    AdvisoryOptionResponseType,
    CreatePortfolioAPI,
    DocumentType,
    SaveInvestmentApplicationDataParams,
    SelectedStrategy,
} from './CreatePortfolioManager.types';

import { memberIdSelector } from '../../redux-store/auth/authSelectors';
import { IPersonalDetails } from '../../core/types/api/mix';
import { generateDFSClientId, getClientId } from '../../utils';
import { useThreeBProductType } from '../../hooks/isThreeBProduct';
import { CreatePortfolioManagerReducer, initialState, LOCAL_STORAGE_KEY } from './CreatePortfolioManager.reducer';
import Preloader from '../../components/Preloader';
import ServiceManager from '../../services/ServiceManager';

type TCreatePortfolioProps = {
    children: ReactNode;
    investmentApplicationId?: number;
    contactGroupId?: number;
    investorProfileClientId?: number;
    portfolioId?: number | string;
    changePlan?: boolean;
}

export const CreatePortfolioContext = createContext<CreatePortfolioAPI>(null as any);

const getMainClientData = (fullClientData: IPersonalDetails) => (fullClientData ? {
    id: fullClientData?.id,
    age: Math.ceil(moment(moment()
        .format('YYYY-MM-DD'))
        .diff(moment(fullClientData?.details?.personalInformation?.dateOfBirth), 'years', true)),
    firstName: `${fullClientData?.details?.personalInformation?.firstName}`,
    lastName: `${fullClientData?.details?.personalInformation?.lastName}`,
    name: `${fullClientData?.details?.personalInformation?.firstName} ${fullClientData?.details?.personalInformation?.lastName}`,
    countryOfResidence: fullClientData?.details?.personalInformation?.primaryAddressCountry,
    taxData: fullClientData?.details?.taxSituation,
    gender: fullClientData?.details?.personalInformation?.gender,
    nationality: fullClientData?.details?.personalInformation?.identification?.[0]?.countryOfNationality,
    isZurichEmployee: fullClientData?.details?.personalInformation?.zurichMA || false,
    destinataryVita: fullClientData?.details?.personalInformation?.destinedLife || false,
    affiliatedPensionFund: fullClientData?.details?.personalInformation?.affiliatedPensionFund,
    employmentContinuation: fullClientData?.details?.personalInformation?.employmentContinuation,
} : undefined);

export const CreatePortfolioManager = ({
    children, investmentApplicationId, changePlan, contactGroupId, investorProfileClientId, portfolioId,
}: TCreatePortfolioProps) => {
    const [state, dispatch] = useReducer(CreatePortfolioManagerReducer, initialState());

    const { dfsClientId } = useParams();
    const clientId = getClientId(dfsClientId);
    const memberId = useReduxSelector(memberIdSelector);
    const { t, i18n: { language } } = useTranslation();

    const [isResumeRequired, setResumeRequired] = useState(!!investmentApplicationId);
    const [isZIVVChangePlanStarted, setIsZIVVChangePlanStarted] = useState(!!changePlan);

    const { advisoryOptions } = useGetListItemsByKey([
        AdvisoryOptionTypesEnum.CATEGORY,
        AdvisoryOptionTypesEnum.INSTITUTE,
        AdvisoryOptionTypesEnum.STRATEGY,
        AdvisoryOptionTypesEnum.REAL_STATE_PROPERTY,
    ]);

    // Init Hooks
    useEffect(() => {
        sessionStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state));
    }, [state]);

    // Mix
    const {
        isLoadingProducts,
        groupedProducts,
        products,
        errorProducts,
    } = useProductsList(clientId);

    const currentInvestmentApplicationSelector = useMemo(() => (state?.applications?.[state?.currentInvestmentApplicationId as number]), [state?.applications, state?.currentInvestmentApplicationId]);
    const contactGroupIdSelector = useMemo(() => contactGroupId || currentInvestmentApplicationSelector?.contactGroupId || currentInvestmentApplicationSelector?.investmentDescription?.contactGroupId, [currentInvestmentApplicationSelector?.contactGroupId, currentInvestmentApplicationSelector?.investmentDescription?.contactGroupId, contactGroupId]);

    const { data: contactGroups } = useGetCustomService({
        service: 'customClientService',
        api: 'getContactGroups',
        payload: [{
            contactId: clientId,
            createThreeB: true,
            createMortgage: false,
        }],
        enabled: !!clientId && !!contactGroupIdSelector,
        gcTime: 10 * 60 * 1000,
        staleTime: 2 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    const selectedContactGroupSelector = useMemo(
        () => (contactGroups?.data?.find((item) => (item.groupId === contactGroupIdSelector))),
        [contactGroupIdSelector, contactGroups?.data],
    );

    const sortedContactGroupUsers = useMemo(
        () => Object.entries(selectedContactGroupSelector?.owners || {})
            .sort((a, b) => +a - +b)
            .map(([key, value]) => ({
                id: key,
                name: value,
            })),
        [selectedContactGroupSelector],
    );

    const {
        mainOwner, jaClient, isLoading: clientDetailsLoading, error: clientDetailsFetchError,
    } = useContactGroupOwners({ connectionGroupId: contactGroupIdSelector as number, clientId });

    const currentClientData = useMemo(() => mainOwner?.clientData, [mainOwner?.clientData]);
    const jointClientData = useMemo(() => jaClient?.clientData, [jaClient?.clientData]);

    const targetInvestorProfileClientIdSelector = useMemo(() => state?.investorProfileClientId || currentClientData?.id, [state.investorProfileClientId, currentClientData?.id]);
    const targetInvestorProfileClient = useMemo(() => (state?.investorProfileClientId === Number(sortedContactGroupUsers?.[0]?.id) ? currentClientData : jointClientData), [state?.investorProfileClientId, sortedContactGroupUsers?.[0]?.id, currentClientData, jointClientData]);
    const targetInvestorProfileOtherClient = useMemo(() => (state?.investorProfileClientId !== Number(sortedContactGroupUsers?.[0]?.id) ? currentClientData : jointClientData), [state?.investorProfileClientId, sortedContactGroupUsers?.[0]?.id, currentClientData, jointClientData]);

    const { refetch: advisoryDataRefetch, isFetching: advisoryDataLoading } = usePostAdvisoryProcessData({
        contactGroupId: contactGroupIdSelector,
        contactId: targetInvestorProfileClientIdSelector,
        isEnabled: false,
    });

    const { getInvestmentApplication, getAdvisory } = useCachedAPI();

    // Selectors
    const containerIdSelector = sessionStorage.getItem(APPLICATION_CONTAINER_ID);
    const currentInvestmentDescriptionSelector = useMemo(() => (currentInvestmentApplicationSelector?.investmentDescription), [currentInvestmentApplicationSelector?.investmentDescription]);
    const currentSelectedStrategySelector = useMemo(() => (currentInvestmentApplicationSelector?.strategy), [currentInvestmentApplicationSelector?.strategy]);
    const currentRecommendedStrategySelector = useMemo(() => (currentInvestmentApplicationSelector?.recommendedStrategy), [currentInvestmentApplicationSelector?.recommendedStrategy]);
    const advisoryIdSelector = useMemo(() => (state.advisoryData?.id), [state.advisoryData?.id]);
    const advisoryDataSelector = useMemo(() => (state.advisoryData), [state.advisoryData, language]);
    const isAdvisoryProcessSelector = useMemo(() => (state.shouldInitiateAdvisoryProcess === 1), [state.shouldInitiateAdvisoryProcess]);
    const advisoryUploadedDocumentSelector = useMemo(() => state.uploadedAdvisoryDocument, [state.uploadedAdvisoryDocument]);
    const selectedProductGroupSelector = useMemo(
        () => groupedProducts?.find(({ id }) => ((currentInvestmentApplicationSelector?.investmentDescription?.productId === id) || (id === 999999 && PRODUCT_VALUES_FOR_3B?.includes(currentInvestmentApplicationSelector?.investmentDescription?.productId as number)))),
        [currentInvestmentApplicationSelector?.investmentDescription?.productId, groupedProducts],
    );
    const selectedProductSelector = useMemo(() => products?.find(({ id }) => (currentInvestmentApplicationSelector?.investmentDescription?.productId === id)), [currentInvestmentApplicationSelector?.investmentDescription?.productId, products]);
    const selectedProductGroupIdSelector = useMemo(() => (selectedProductGroupSelector?.id ?? -1), [selectedProductGroupSelector?.id]);
    const selectedProductIdSelector = useMemo(() => (selectedProductSelector?.id ?? -1), [selectedProductSelector?.id]);
    const selectedProductTypeSelector = useMemo(() => (selectedProductGroupSelector?.settings?.productKey?.toLowerCase()), [selectedProductGroupSelector?.settings?.productKey]);
    const selectedProductDescSelector = useMemo(() => (selectedProductSelector?.description), [selectedProductSelector?.description]);
    const isDependantOnAffiliatedWithPensionFundSelector = useMemo(() => (!!selectedProductGroupSelector?.settings?.isDependantOnAffiliatedWithPensionFund), [selectedProductGroupSelector?.settings?.isDependantOnAffiliatedWithPensionFund]);

    const is3bProductGroupSelector = useMemo(() => (arrayContains([selectedProductTypeSelector, selectedProductSelector?.settings?.productKey], PRODUCTS.bbb)), [selectedProductTypeSelector, selectedProductSelector]);
    const isVbVbPremiumProductGroupSelector = useMemo(() => (arrayContains([selectedProductTypeSelector, selectedProductSelector?.settings?.productKey], PRODUCTS.fz) || arrayContains([selectedProductTypeSelector, selectedProductSelector?.settings?.productKey], PRODUCTS.fzp)), [selectedProductTypeSelector, selectedProductSelector]);
    const is3aProductGroupSelector = useMemo(() => (arrayContains([selectedProductTypeSelector, selectedProductSelector?.settings?.productKey], PRODUCTS.aaa)), [selectedProductTypeSelector, selectedProductSelector]);
    const is3aPremiumProductGroupSelector = useMemo(() => (arrayContains([selectedProductTypeSelector, selectedProductSelector?.settings?.productKey], PRODUCTS.aaap)), [selectedProductTypeSelector, selectedProductSelector]);

    const isJointAccountSelector = useMemo(() => (selectedContactGroupSelector?.groupName === ContactGroupEnum.ja), [selectedContactGroupSelector]);
    const currentClientSelector = useMemo(() => (getMainClientData(currentClientData)), [currentClientData]);
    const jointClientSelector = useMemo(() => (getMainClientData(jointClientData)), [jointClientData]);
    const targetInvestorProfileClientSelector = useMemo(() => (getMainClientData(targetInvestorProfileClient)), [targetInvestorProfileClient]);
    const targetInvestorProfileOtherClientSelector = useMemo(() => (getMainClientData(targetInvestorProfileOtherClient)), [targetInvestorProfileOtherClient]);
    const isPageLoadingSelector = clientDetailsLoading || isLoadingProducts;
    const pageErrorsSelector = useMemo(
        () => (
            clientDetailsFetchError || errorProducts
        ),
        [clientDetailsFetchError || errorProducts],
    );

    const targetInvestorProfileDFSClientIdSelector = useMemo(
        () => (
            state?.investorProfileClientId || currentClientData?.id
                ? generateDFSClientId(state?.investorProfileClientId || currentClientData?.id)
                : undefined),
        [state.investorProfileClientId, currentClientData?.id],
    );

    const firstNotPKNewProductSelector = useMemo(() => {
        if (!is3bProductGroupSelector) {
            return undefined;
        }
        const lastNotPKApplicationId = Object.keys(state.applications)
            .find((applicationId) => {
                const application = state.applications[applicationId];
                const productId = application?.investmentDescription?.productId;
                const product = products?.find(({ id }) => (productId === id));

                return product?.externalId !== THREEBTYPES.pk && !application?.investmentDescription?.ibanReservation;
            });

        if (!lastNotPKApplicationId) {
            return undefined;
        }

        return products?.find(({ id }) => (state.applications?.[lastNotPKApplicationId]?.investmentDescription?.productId === id));
    }, [state.applications, products, is3bProductGroupSelector]);

    // advisoty options
    const categoryOptions = useMemo(() => advisoryOptions?.find(option => option?.key === AdvisoryOptionTypesEnum.CATEGORY), [advisoryOptions]);
    const instituteOptions = useMemo(() => advisoryOptions?.find(option => option?.key === AdvisoryOptionTypesEnum.INSTITUTE), [advisoryOptions]);
    const strategyOptions = useMemo(() => advisoryOptions?.find(option => option?.key === AdvisoryOptionTypesEnum.STRATEGY), [advisoryOptions]);
    const realStatePropertyOptions = useMemo(() => advisoryOptions?.find(option => option?.key === AdvisoryOptionTypesEnum.REAL_STATE_PROPERTY), [advisoryOptions]);

    const { strategyAllocations } = useGetStrategyAllocations();

    const proxyContactId = useMemo(() => (Object?.keys(selectedContactGroupSelector?.owners || {})?.find(key => +key !== clientId)), [selectedContactGroupSelector?.owners]);

    const { data: jointAccountPortfolios } = useGetJointPortfolios({
        clientId,
        proxyId: parseInt(proxyContactId || '', 10),
    });

    const getPortfolios = useCallback(
        () => jointAccountPortfolios
            ?.filter(
                (portfolio: any) => getPortfolioStatus({ portfolioStatusId: portfolio?.portfolioStatusId }) === 'active'
                        && [...PRODUCT_VALUES_FOR_3A_VB]?.includes(
                            portfolio?.product?.id,
                        ),
            )
            ?.map((p: any) => ({
                portfolioId: p?.id,
                productId: p?.product?.id,
                value: p?.currentValue || 0,
            })),
        [jointAccountPortfolios],
    );
    const portfolios = getPortfolios() || [];

    const totalPillar3aAmountSelector = useMemo(
        () => portfolios
            ?.filter(
                (portfolio) => [PRODUCT_VALUES_FOR_3A_VB_ENUM['3A'], PRODUCT_VALUES_FOR_3A_VB_ENUM['3A_PREMIUM']]?.includes(portfolio?.productId),
            )
            .reduce((acc, curr) => acc + (curr.value || 0), 0),
        [portfolios],
    );

    const totalVBAmountSelector = useMemo(
        () => portfolios
            ?.filter(
                (portfolio) => [PRODUCT_VALUES_FOR_3A_VB_ENUM.VB, PRODUCT_VALUES_FOR_3A_VB_ENUM.VB_PREMIUM]?.includes(portfolio?.productId),
            )
            .reduce((acc, curr) => acc + (curr.value || 0), 0),
        [portfolios],
    );

    useEffect(() => {
        if (isResumeRequired && products?.length > 0) {
            resumeShoppingCardAction();
        }
    }, [isResumeRequired, products]);

    const resumeShoppingCardAction = useCallback(async () => {
        sessionStorage.removeItem('totalAmountOfOtherAssets');
        try {
            const applicationData = await getInvestmentApplication([{ investmentApplicationId }], { withCache: true });

            const {
                productId,
                advisoryDocumentId,
                containerId,
                isJointAccount,
                investorProfileContactId,
            } = applicationData?.investmentDescription || {};

            const selectedProduct = products?.find(({ id }) => (productId === id)) as any;
            const is3B = arrayContains([PRODUCTS.bbb], selectedProduct?.settings?.productKey);

            sessionStorage.setItem(APPLICATION_CONTAINER_ID, containerId);

            const payload = {
                shouldInitiateAdvisoryProcess: 2,
                is3B,
                applications: [applicationData],
                advisoryData: undefined,
                advisoryProcessInProgress: false,
                investmentApplicationId,
                ...isJointAccount ? {
                    investorProfileClientId: investorProfileContactId,
                } : {},
            };

            if (is3B) {
                if (advisoryDocumentId) {
                    const data = await getAdvisory([{ id: advisoryDocumentId }], { withCache: true, staleTime: 10 * 1000 });

                    payload.advisoryData = data;
                    payload.shouldInitiateAdvisoryProcess = 1;

                    if (data?.status === 2) {
                        payload.advisoryProcessInProgress = true;
                    }
                }
            }

            dispatch({
                type: 'resumeShoppingCard',
                payload,
            });
            setResumeRequired(false);
        } catch (error: any) {
            notification.open({ content: `${t('contactGroups.somethingWentWrong')} ${error?.message}`, type: 'error' });
        }
    }, [investmentApplicationId, language, products, getInvestmentApplication, getAdvisory]);

    const setSelectedProductIdAction = useCallback(
        (prodId: number) => {
            const prod = groupedProducts?.find(({ id }) => (prodId === id));

            dispatch({
                type: 'setProductData',
                payload: {
                    productExternalId: prod?.externalId,
                    productId: prodId,
                },
            });
        },
        [groupedProducts],
    );
    const lockNavigationAction = useCallback(() => dispatch({
        type: 'lockNavigation',
    }), []);
    const unlockNavigationAction = useCallback(() => dispatch({
        type: 'unlockNavigation',
    }), []);
    const setAdvisoryProcessAction = useCallback((isAdvisoryProcess: boolean) => dispatch({
        type: 'setAdvisoryProcess',
        payload: isAdvisoryProcess,
    }), []);
    const setAdvisoryDataAction = useCallback((data:AdvisoryResponse | undefined) => dispatch({
        type: 'setAdvisoryData',
        payload: data,
    }), []);
    const setUploadedAdvisoryDocumentAction = useCallback((document: DocumentType | null) => dispatch({
        type: 'setUploadedAdvisoryDocument',
        payload: document,
    }), []);
    const setAdvisoryProcessTypeAction = useCallback((advisoryProcessType: string) => dispatch({
        type: 'setAdvisoryProcessType',
        payload: advisoryProcessType,
    }), []);
    const setConnectionGroupIdAction = useCallback((connectionGroupId: string) => dispatch({
        type: 'setConnectionGroupId',
        payload: connectionGroupId,
    }), []);
    const setShouldInitiateAdvisoryProcessAction = useCallback((shouldInitiateAdvisoryProcess: number) => dispatch({
        type: 'setShouldInitiateAdvisory',
        payload: shouldInitiateAdvisoryProcess,
    }), []);
    const saveRecommendedStrategyAction = useCallback(({ data }) => {
        dispatch({
            type: 'saveRecommendedStrategy',
            payload: data,
        });
    }, []);
    const saveInvestorProfileClientAction = useCallback((contactId:number) => {
        dispatch({
            type: 'saveInvestorProfileClient',
            payload: contactId,
        });
    }, []);
    const saveCurrentInvestmentApplicationIdAction = useCallback((id: number | string) => {
        dispatch({
            type: 'saveCurrentInvestmentApplicationId',
            payload: {
                investmentApplicationId: id,
            },
        });
    }, []);
    const clearProductSetupAction = useCallback(() => {
        dispatch({
            type: 'clearProductSetup',
        });
    }, []);
    const startProductSetupAction = useCallback(async (id: number | string) => {
        if (state?.applications?.[id]) {
            dispatch({
                type: 'startProductSetup',
                payload: {
                    investmentApplicationId: id,
                    applicationData: { },
                },
            });
        } else {
            try {
                const response = await ServiceManager.customInvestmentService('getInvestmentApplication', [{ investmentApplicationId: id }]) as any;

                if (response?.status === STATUS_OK) {
                    dispatch({
                        type: 'startProductSetup',
                        payload: {
                            investmentApplicationId: id,
                            applicationData: { ...response?.data },
                        },
                    });
                }
            } catch (error: any) {
                notification.open({ content: `${t('contactGroups.somethingWentWrong')} ${error?.message}`, type: 'error' });
            }
        }
    }, [state, state?.applications, dispatch]);
    const saveInvestmentApplicationDataAction = useCallback(
        async ({ method, payload, additionalData = {} }:SaveInvestmentApplicationDataParams) => {
            try {
                const payloadByMethod = {
                    saveProductDetails: () => (payload),
                    savePersonalDetails: () => ({
                        investmentApplicationId: state.currentInvestmentApplicationId,
                        ...payload,
                    }),
                    saveStrategyData: () => ({
                        investmentApplicationId: state.currentInvestmentApplicationId,
                        ...payload,
                    }),
                    saveProductData: () => ({
                        investmentApplicationId: state.currentInvestmentApplicationId,
                        ...payload,
                    }),
                    saveAgentData: () => ({
                        investmentApplicationId: state.currentInvestmentApplicationId,
                        ...payload,
                    }),
                    saveKYCDoneByContainer:
                        () => ({
                            id: containerIdSelector,
                            ...payload,
                        }),
                    saveKYCDone:
                        () => ({
                            investmentApplicationId: state.currentInvestmentApplicationId,
                            ...payload,
                        }),
                    saveIdUploadDone:
                        () => ({
                            investmentApplicationId: state.currentInvestmentApplicationId,
                            ...payload,
                        }),
                    saveWithdrawalData:
                        () => ({
                            investmentApplicationId: state.currentInvestmentApplicationId,
                            ...payload,
                        }),
                };

                const requestPayload = payloadByMethod?.[method]?.() ?? payload;
                const response = await ServiceManager.customInvestmentService(method, [requestPayload]) as any;

                if (response?.status === STATUS_OK && method !== 'saveKYCDoneByContainer') {
                    dispatch({
                        type: 'saveInvestmentApplicationData',
                        payload: { ...response?.data, ...additionalData },
                    });
                }

                return response;
            } catch (error:any) {
                notification.open({ content: `${t('contactGroups.somethingWentWrong')} ${error?.message}`, type: 'error' });

                return error;
            }
        },
        [
            clientId,
            memberId,
            selectedProductGroupSelector?.id,
            selectedProductGroupSelector?.externalId,
            state.currentInvestmentApplicationId,
            containerIdSelector,
        ],
    );

    const removeInvestmentApplicationDataAction = useCallback(({ id }: {id: number | string}) => {
        dispatch({
            type: 'removeInvestmentApplicationData',
            payload: {
                investmentApplicationId: id,
            },
        });
    }, []);

    const advisoryProcessInProgressAction = useCallback((payload: boolean) => {
        dispatch({
            type: 'setAdvisoryProcessInProgress',
            payload,
        });
    }, []);

    const startAdvisoryProcessAction = useCallback(async () => {
        if (!state?.advisoryData?.id) {
            const response = await advisoryDataRefetch();

            setAdvisoryDataAction(response?.data?.data);
        } else {
            setAdvisoryDataAction(state?.advisoryData);
        }
        advisoryProcessInProgressAction(true);
    }, [state?.advisoryData]);

    const saveAdvisoryDataAction = useCallback(
        async ({ key, payload, setPageErrors }: {key: string, payload: keyof AdvisoryMetadataResponse, setPageErrors?: (value: SetStateAction<TPageError | undefined>) => void}) => {
            try {
                const response:any = await ServiceManager.customAdvisoryServices('updateAdvisoryProcessData', [{
                    advisoryId: advisoryIdSelector,
                    key,
                    payload,
                }]);

                if (response?.status === STATUS_OK) {
                    dispatch({
                        type: 'setAdvisoryData',
                        payload: response?.data,
                    });
                }

                return { status: response.status };
            } catch (error:any) {
                setPageErrors?.({
                    message: error?.message || '',
                });
                notification.open({ content: `${error?.message || t('contactGroups.somethingWentWrong')}`, type: 'error' });

                return { error };
            }
        },
        [advisoryIdSelector],
    );

    const saveInvestmentApplicationDataToStoreAction = useCallback(({ payload }: {payload: any}) => {
        dispatch({
            type: 'saveInvestmentApplicationData',
            payload,
        });
    }, []);

    const startZIVVChangePlanAdvisoryProcessAction = useCallback(() => {
        dispatch({
            type: 'startZIVVChangePlanAdvisoryProcess',
            payload: { contactGroupId, investorProfileClientId, portfolioId },
        });
        setIsZIVVChangePlanStarted(false);
    }, [dispatch, setIsZIVVChangePlanStarted]);

    const setContinueDisabledFromShoppingCartAction = useCallback((payload) => {
        dispatch({
            type: 'setContinueDisabledFromShoppingCart',
            payload,
        });
    }, [dispatch]);
    const setContainerApplicationsFromShoppingCart = useCallback((payload) => {
        dispatch({
            type: 'setContainerApplications',
            payload,
        });
    }, [dispatch]);

    const setDefaultAssetManagementFeeInCNP = useCallback(({ investmentApplicationId: investmentAppId, fee }) => {
        dispatch({
            type: 'setDefaultAssetManagementFee',
            payload: {
                investmentApplicationId: investmentAppId,
                fee,
            },
        });
    }, [dispatch]);

    useEffect(() => {
        if (isZIVVChangePlanStarted) {
            startZIVVChangePlanAdvisoryProcessAction();
        }
    }, [isZIVVChangePlanStarted]);

    const {
        isZivZicAzp, isZic, isAzp, isZiv, isZifd, isZicAzp, isPk, isZivZifd, isZivAzp, isJA, isAzpI, isAzpD, isAzpAlt,
    } = useThreeBProductType({ product: selectedProductSelector });

    useEffect(() => () => {
        sessionStorage.removeItem(LOCAL_STORAGE_KEY);
    }, []);

    const cachedState = useMemo(() => (
        {
            state: {
                ...state,
            },
            actions: {
                startAdvisoryProcessAction,
                resumeShoppingCardAction,
                setSelectedProductIdAction,
                setAdvisoryProcessAction,
                setAdvisoryProcessTypeAction,
                setConnectionGroupIdAction,
                saveInvestmentApplicationDataAction,
                saveInvestorProfileClientAction,
                saveRecommendedStrategyAction,
                setShouldInitiateAdvisoryProcessAction,
                lockNavigationAction,
                setAdvisoryDataAction,
                setUploadedAdvisoryDocumentAction,
                unlockNavigationAction,
                saveAdvisoryDataAction,
                saveCurrentInvestmentApplicationIdAction,
                startProductSetupAction,
                clearProductSetupAction,
                advisoryProcessInProgressAction,
                removeInvestmentApplicationDataAction,
                saveInvestmentApplicationDataToStoreAction,
                setContinueDisabledFromShoppingCartAction,
                setContainerApplicationsFromShoppingCart,
                setDefaultAssetManagementFeeInCNP,
            },
            selectors: {
                isZivZicAzp,
                isZic,
                isAzp,
                isZiv,
                isZifd,
                isZicAzp,
                isPk,
                isZivZifd,
                isZivAzp,
                isJA,
                isAzpI,
                isAzpD,
                isAzpAlt,
                firstNotPKNewProductSelector,
                containerIdSelector,
                targetInvestorProfileClientIdSelector,
                targetInvestorProfileDFSClientIdSelector,
                currentRecommendedStrategySelector,
                currentSelectedStrategySelector: currentSelectedStrategySelector as SelectedStrategy,
                currentInvestmentApplicationSelector,
                currentInvestmentDescriptionSelector: currentInvestmentDescriptionSelector as InvestmentDescription,
                advisoryDataSelector,
                advisoryIdSelector,
                isAdvisoryProcessSelector,
                is3aProductGroupSelector,
                is3bProductGroupSelector,
                isVbVbPremiumProductGroupSelector,
                is3aPremiumProductGroupSelector,
                selectedProductDescSelector,
                currentClientSelector,
                jointClientSelector,
                selectedProductTypeSelector,
                selectedProductSelector,
                selectedProductGroupSelector,
                isJointAccountSelector,
                selectedProductGroupIdSelector,
                selectedProductIdSelector,
                contactGroupIdSelector: contactGroupIdSelector as number,
                isPageLoadingSelector,
                pageErrorsSelector,
                advisoryUploadedDocumentSelector: advisoryUploadedDocumentSelector as DocumentType,
                isDependantOnAffiliatedWithPensionFundSelector,
                currentClientPersonalDetailsDataSelector: currentClientData,
                targetInvestorProfileClientSelector,
                targetInvestorProfileOtherClientSelector,
                jointClientPersonalDetailsDataSelector: jointClientData,
                advisoryOptions: {
                    category: categoryOptions as AdvisoryOptionResponseType,
                    institute: instituteOptions as AdvisoryOptionResponseType,
                    strategy: strategyOptions as AdvisoryOptionResponseType,
                    realEstateProperties: realStatePropertyOptions as AdvisoryOptionResponseType,
                },
                strategyAllocations,
                proxyContactId,
                advisoryProcessInProgressSelector: state?.advisoryProcessInProgress as boolean,
                isAdvisoryDataLoading: advisoryDataLoading,
                changePlan,
                isLoadingProducts,
                groupedProducts,
                products,
                errorProducts,
                mainOwner,
                jaClient,
                totalPillar3aAmountSelector,
                totalVBAmountSelector,
            },
        }
    ), [
        state,
        startAdvisoryProcessAction,
        resumeShoppingCardAction,
        setSelectedProductIdAction,
        setAdvisoryProcessAction,
        setAdvisoryProcessTypeAction,
        setConnectionGroupIdAction,
        saveInvestmentApplicationDataAction,
        saveInvestorProfileClientAction,
        saveRecommendedStrategyAction,
        setShouldInitiateAdvisoryProcessAction,
        lockNavigationAction,
        setAdvisoryDataAction,
        setUploadedAdvisoryDocumentAction,
        unlockNavigationAction,
        saveAdvisoryDataAction,
        saveCurrentInvestmentApplicationIdAction,
        startProductSetupAction,
        clearProductSetupAction,
        advisoryProcessInProgressAction,
        removeInvestmentApplicationDataAction,
        saveInvestmentApplicationDataToStoreAction,
        setContinueDisabledFromShoppingCartAction,
        setContainerApplicationsFromShoppingCart,
        setDefaultAssetManagementFeeInCNP,
        isZivZicAzp,
        isZic,
        isAzp,
        isZiv,
        isZifd,
        isZicAzp,
        isPk,
        isZivZifd,
        isZivAzp,
        isJA,
        isAzpI,
        isAzpD,
        isAzpAlt,
        firstNotPKNewProductSelector,
        containerIdSelector,
        targetInvestorProfileClientIdSelector,
        targetInvestorProfileDFSClientIdSelector,
        currentRecommendedStrategySelector,
        currentSelectedStrategySelector,
        currentInvestmentApplicationSelector,
        currentInvestmentDescriptionSelector,
        advisoryDataSelector,
        advisoryIdSelector,
        isAdvisoryProcessSelector,
        is3aProductGroupSelector,
        is3bProductGroupSelector,
        isVbVbPremiumProductGroupSelector,
        is3aPremiumProductGroupSelector,
        selectedProductDescSelector,
        currentClientSelector,
        jointClientSelector,
        selectedProductTypeSelector,
        selectedProductSelector,
        selectedProductGroupSelector,
        isJointAccountSelector,
        selectedProductGroupIdSelector,
        selectedProductIdSelector,
        contactGroupIdSelector,
        isPageLoadingSelector,
        pageErrorsSelector,
        advisoryUploadedDocumentSelector,
        isDependantOnAffiliatedWithPensionFundSelector,
        currentClientData,
        targetInvestorProfileClientSelector,
        targetInvestorProfileOtherClientSelector,
        jointClientData,
        categoryOptions,
        instituteOptions,
        strategyOptions,
        realStatePropertyOptions,
        strategyAllocations,
        proxyContactId,
        state?.advisoryProcessInProgress,
        advisoryDataLoading,
        changePlan,
        isLoadingProducts,
        groupedProducts,
        products,
        errorProducts,
        mainOwner,
        jaClient,
        totalPillar3aAmountSelector,
        totalVBAmountSelector,
    ]);

    return (
        <CreatePortfolioContext.Provider value={cachedState}>
            <Preloader isLoading={isResumeRequired || isZIVVChangePlanStarted}>
                {children}
            </Preloader>
        </CreatePortfolioContext.Provider>
    );
};
CreatePortfolioManager.LOCAL_STORAGE_KEY = LOCAL_STORAGE_KEY;

export const useCreatePortfolio = (): CreatePortfolioAPI => useContext(CreatePortfolioContext);
